import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw, AlertTriangle, ChevronRight, Download } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';

/**
 * Google Ads · Dönüşüm Kırılımı — kampanya bazında harcama, dönüşüm ve dönüşüm değerinin
 * hangi dönüşüm eyleminden geldiği. Satın alma dışı birincil eylemler (sepete ekle vb.)
 * "Dönüşümler" sütununa girip ROAS'ı şişiriyorsa burada görünür.
 *
 * Backend: GET /api/google-ads-donusum/overview?start&end (canlı Google Ads API, 20 dk önbellek).
 * Seçili markada çalışır. Bilerek yalnız açık tema (panel raporları gibi).
 */

const API_BASE = (() => {
  const raw = (process.env.REACT_APP_API_URL || 'https://api.glowytics.com').trim();
  return raw.replace(/\/+$/, '').replace(/\/api$/, '');
})();

const C = {
  ground: '#F6F8FF', card: '#FFFFFF', line: '#E5E7EB', hair: '#F3F4F6',
  ink: '#111827', ink2: '#374151', muted: '#6B7280', faint: '#9CA3AF',
  accent: '#436AEB', accentSoft: '#EEF2FE', warn: '#B45309', warnSoft: '#FFF7ED', down: '#B91C1C'
};
// Satın alma eylemleri önce gelir, ilk renk onlarındır.
const PALET = ['#436AEB', '#eb6834', '#1baf7a', '#eda100', '#9b59d0', '#0ea5b7', '#C4C9D4'];
const FONT = '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

const KATEGORI = {
  PURCHASE: 'Satın alma', ADD_TO_CART: 'Sepete ekle', BEGIN_CHECKOUT: 'Ödemeye başla',
  PAGE_VIEW: 'Sayfa görüntüleme', DOWNLOAD: 'Uygulama indirme', SUBSCRIBE_PAID: 'Abonelik',
  SIGNUP: 'Kayıt', LEAD: 'Potansiyel müşteri', SUBMIT_LEAD_FORM: 'Form', CONTACT: 'İletişim',
  PHONE_CALL_LEAD: 'Telefon', DEFAULT: 'Diğer', ENGAGEMENT: 'Etkileşim', STORE_VISIT: 'Mağaza ziyareti'
};
const katAd = (k) => KATEGORI[k] || k;
const STRATEJI = {
  TARGET_SPEND: 'Maks. tıklama', MAXIMIZE_CONVERSIONS: 'Maks. dönüşüm',
  MAXIMIZE_CONVERSION_VALUE: 'Maks. dönüşüm değeri', TARGET_ROAS: 'Hedef ROAS', TARGET_CPA: 'Hedef EBM',
  MANUAL_CPC: 'Manuel TBM', TARGET_IMPRESSION_SHARE: 'Hedef gösterim payı', MANUAL_CPM: 'Manuel BGBM'
};
const KANAL = { SEARCH: 'Arama', PERFORMANCE_MAX: 'PMax', DEMAND_GEN: 'Demand Gen', DISPLAY: 'Görüntülü', VIDEO: 'Video', SHOPPING: 'Alışveriş' };
const KAYNAK = { WEBPAGE: 'Google etiketi', GOOGLE_ANALYTICS_4_PURCHASE: 'GA4', GOOGLE_ANALYTICS_4_CUSTOM: 'GA4' };
const kaynakAd = (t) => KAYNAK[t] || (t.startsWith('ANDROID') || t.includes('APP') ? 'Uygulama' : t.replace(/_/g, ' ').toLowerCase());

const nf = (n, d = 0) => Number(n || 0).toLocaleString('tr-TR', { maximumFractionDigits: d, minimumFractionDigits: d });
const tl = (n) => `₺${nf(n)}`;
const tlShort = (n) => {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1e6) return `₺${nf(v / 1e6, 2)}M`;
  if (Math.abs(v) >= 1e4) return `₺${nf(v / 1e3)}B`;
  return tl(v);
};
const pct = (x) => `%${nf((x || 0) * 100)}`;
const roas = (v, c) => (c > 0 ? `${nf(v / c, 1)}×` : '–');
const AY = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
const gunEtiket = (s) => `${Number(s.slice(8, 10))} ${AY[Number(s.slice(5, 7)) - 1]} ${s.slice(0, 4)}`;
const isoGun = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
function aralik(gun) {
  const end = new Date(Date.now() - 86400000);
  const start = new Date(end.getTime() - (gun - 1) * 86400000);
  return { start: isoGun(start), end: isoGun(end) };
}

const kart = { background: C.card, border: `1px solid ${C.line}`, borderRadius: 12, boxShadow: '0 1px 2px rgba(16,24,40,.04)' };
const th = { textAlign: 'right', padding: '10px 12px', fontSize: 12, fontWeight: 600, color: C.muted, borderBottom: `1px solid ${C.line}`, whiteSpace: 'nowrap', background: '#FAFBFF' };
const td = { textAlign: 'right', padding: '10px 12px', fontSize: 13, color: C.ink2, borderBottom: `1px solid ${C.hair}`, whiteSpace: 'nowrap' };

function Secici({ secenekler, deger, onChange }) {
  return (
    <div style={{ display: 'inline-flex', background: C.hair, borderRadius: 10, padding: 3 }}>
      {secenekler.map(([k, label]) => (
        <button key={k} onClick={() => onChange(k)} style={{
          border: 0, cursor: 'pointer', fontFamily: FONT, fontSize: 13, padding: '6px 12px', borderRadius: 8,
          background: deger === k ? C.card : 'transparent', color: deger === k ? C.ink : C.muted,
          fontWeight: deger === k ? 600 : 500, boxShadow: deger === k ? '0 1px 2px rgba(16,24,40,.08)' : 'none'
        }}>{label}</button>
      ))}
    </div>
  );
}

function Kpi({ etiket, deger, alt, vurgu }) {
  return (
    <div style={{ ...kart, padding: '16px 18px', borderColor: vurgu ? '#FED7AA' : C.line, background: vurgu ? C.warnSoft : C.card }}>
      <div style={{ fontSize: 12, color: C.muted, fontWeight: 500 }}>{etiket}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color: vurgu ? C.warn : C.ink, marginTop: 6, letterSpacing: -0.5 }}>{deger}</div>
      {alt && <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>{alt}</div>}
    </div>
  );
}

export default function GoogleAdsDonusumKirilimi() {
  const brandId = typeof window !== 'undefined' ? localStorage.getItem('currentBrandId') : null;
  const [gun, setGun] = useState('30');
  const [metrik, setMetrik] = useState('value'); // value | conv
  const [kapsam, setKapsam] = useState('primary'); // primary | all
  const [veri, setVeri] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [hata, setHata] = useState(null);
  const [kapali, setKapali] = useState(() => new Set()); // daraltılmış kampanya anahtarları

  // PDF = tarayıcının yazdırma penceresi; @media print sidebar'ı ve düğmeleri gizler.
  // Dosya adı document.title'dan gelir, yazdırma boyunca geçici olarak değiştirilir.
  const pdfIndir = () => {
    const eski = document.title;
    if (veri) document.title = `${veri.brand.name} - Google Ads Donusum Kirilimi - ${veri.meta.start} - ${veri.meta.end}`;
    const geri = () => { document.title = eski; window.removeEventListener('afterprint', geri); };
    window.addEventListener('afterprint', geri);
    window.print();
  };

  const yukle = useCallback(async (refresh = false) => {
    if (!brandId) return;
    setYukleniyor(true); setHata(null);
    try {
      const { start, end } = aralik(Number(gun));
      const res = await fetch(
        `${API_BASE}/api/google-ads-donusum/overview?start=${start}&end=${end}${refresh ? '&refresh=1' : ''}`,
        { credentials: 'include', headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId } }
      );
      const j = await res.json();
      if (!res.ok) throw new Error(j.error || `HTTP ${res.status}`);
      setVeri(j);
    } catch (e) {
      setHata(e.message);
    } finally {
      setYukleniyor(false);
    }
  }, [brandId, gun]);

  useEffect(() => { yukle(); }, [yukle]);

  const hesap = useMemo(() => {
    if (!veri) return null;
    const cokHesap = veri.accounts.filter((a) => a.ok).length > 1;
    const kampanyalar = [];
    const eylemler = new Map(); // key -> { key, name, category, ... ayar }
    for (const a of veri.accounts) {
      if (!a.ok) continue;
      const ayar = new Map(a.actions.map((x) => [x.id, x]));
      const hedef = new Map();
      for (const g of a.goals) {
        if (!hedef.has(g.campaignId)) hedef.set(g.campaignId, new Set());
        hedef.get(g.campaignId).add(g.category);
      }
      const byC = new Map();
      for (const c of a.campaigns) {
        const k = `${a.customerId}:${c.id}`;
        const row = { ...c, key: k, hesap: a.customerId, ad: cokHesap ? `${c.name} · ${a.customerId}` : c.name, eylem: {}, hedef: [...(hedef.get(c.id) || [])] };
        byC.set(c.id, row); kampanyalar.push(row);
      }
      for (const b of a.breakdown) {
        const ek = `${a.customerId}:${b.actionId}`;
        if (!eylemler.has(ek)) {
          const s = ayar.get(b.actionId) || {};
          eylemler.set(ek, { key: ek, name: b.action, category: b.category, ...s, id: b.actionId, conv: 0, value: 0, allConv: 0, allValue: 0 });
        }
        const e = eylemler.get(ek);
        e.conv += b.conv; e.value += b.value; e.allConv += b.allConv; e.allValue += b.allValue;
        const c = byC.get(b.campaignId);
        if (c) c.eylem[ek] = b;
      }
      // Bu dönemde hiç veri gelmemiş ama ayarlı eylemler de tabloda görünsün.
      for (const s of a.actions) {
        const ek = `${a.customerId}:${s.id}`;
        if (!eylemler.has(ek)) eylemler.set(ek, { key: ek, ...s, conv: 0, value: 0, allConv: 0, allValue: 0 });
      }
    }
    const sira = [...eylemler.values()].sort((x, y) =>
      (y.category === 'PURCHASE') - (x.category === 'PURCHASE') || (y.allValue + y.allConv) - (x.allValue + x.allConv));
    const veriliEylem = sira.filter((e) => e.allConv > 0 || e.allValue > 0);
    const renk = new Map(veriliEylem.map((e, i) => [e.key, PALET[Math.min(i, PALET.length - 1)]]));

    for (const c of kampanyalar) {
      c.satinAlmaDeger = 0; c.satinAlmaConv = 0; c.digerDeger = 0; c.digerConv = 0; c.satinAlmaEylemSayisi = 0;
      for (const [ek, b] of Object.entries(c.eylem)) {
        if (b.category === 'PURCHASE') {
          c.satinAlmaDeger += b.value; c.satinAlmaConv += b.conv;
          if (b.conv > 0) c.satinAlmaEylemSayisi += 1;
        } else { c.digerDeger += b.value; c.digerConv += b.conv; }
        void ek;
      }
      c.sorun = c.digerConv > 0.5 || c.satinAlmaEylemSayisi > 1;
    }
    kampanyalar.sort((x, y) => y.cost - x.cost);
    const top = kampanyalar.reduce((t, c) => {
      t.cost += c.cost; t.value += c.value; t.conv += c.conv; t.allValue += c.allValue; t.allConv += c.allConv;
      t.satinAlmaDeger += c.satinAlmaDeger; t.digerDeger += c.digerDeger; t.digerConv += c.digerConv;
      return t;
    }, { cost: 0, value: 0, conv: 0, allValue: 0, allConv: 0, satinAlmaDeger: 0, digerDeger: 0, digerConv: 0 });
    return { kampanyalar, sira, veriliEylem, renk, top, hatalar: veri.accounts.filter((a) => !a.ok) };
  }, [veri]);

  const alan = metrik === 'value' ? (kapsam === 'primary' ? 'value' : 'allValue') : (kapsam === 'primary' ? 'conv' : 'allConv');
  const bicim = (n) => (metrik === 'value' ? tlShort(n) : nf(n, n > 0 && n < 10 ? 1 : 0));

  const sutunlar = useMemo(() => {
    if (!hesap) return [];
    return hesap.veriliEylem.filter((e) => hesap.kampanyalar.some((c) => (c.eylem[e.key]?.[alan] || 0) > 0));
  }, [hesap, alan]);

  const grafik = useMemo(() => {
    if (!hesap) return [];
    return hesap.kampanyalar
      .filter((c) => sutunlar.some((e) => (c.eylem[e.key]?.[alan] || 0) > 0))
      .slice(0, 16)
      .map((c) => {
        const r = { ad: c.ad.length > 34 ? `${c.ad.slice(0, 33)}…` : c.ad };
        for (const e of sutunlar) r[e.key] = c.eylem[e.key]?.[alan] || 0;
        return r;
      });
  }, [hesap, sutunlar, alan]);

  const sorunlu = hesap ? hesap.kampanyalar.filter((c) => c.sorun) : [];

  return (
    <div id="gad-rapor" style={{ background: C.ground, minHeight: '100%', fontFamily: FONT, color: C.ink, padding: '28px 32px 48px', fontVariantNumeric: 'tabular-nums' }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 13, color: C.muted, fontWeight: 500 }}>{veri?.brand?.name || 'Google Ads'} · Google Ads</div>
          <h1 style={{ fontSize: 26, fontWeight: 700, margin: '4px 0 0', letterSpacing: -0.5 }}>Dönüşüm Kırılımı</h1>
          <div style={{ fontSize: 13, color: C.muted, marginTop: 6 }}>
            {veri ? `${gunEtiket(veri.meta.start)} – ${gunEtiket(veri.meta.end)} · canlı Google Ads API${veri.meta.cached ? ' (önbellek)' : ''}` : 'Kampanya bazında dönüşüm ve dönüşüm değerinin hangi eylemden geldiği'}
          </div>
        </div>
        <div className="gad-yazdirma-yok" style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <Secici secenekler={[['7', '7 gün'], ['14', '14 gün'], ['30', '30 gün'], ['90', '90 gün']]} deger={gun} onChange={setGun} />
          <button onClick={() => yukle(true)} disabled={yukleniyor} title="Önbelleği atla" style={{
            ...kart, display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px', cursor: 'pointer',
            fontFamily: FONT, fontSize: 13, color: C.ink2
          }}>
            <RefreshCw size={14} style={{ animation: yukleniyor ? 'spin 1s linear infinite' : 'none' }} /> Yenile
          </button>
          <button onClick={pdfIndir} disabled={!hesap} style={{
            ...kart, display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px', cursor: hesap ? 'pointer' : 'default',
            fontFamily: FONT, fontSize: 13, color: '#fff', background: C.accent, borderColor: C.accent, opacity: hesap ? 1 : 0.5
          }}>
            <Download size={14} /> PDF indir
          </button>
        </div>
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}
        @media print {
          @page { size: A4 landscape; margin: 8mm; }
          body * { visibility: hidden; }
          #gad-rapor, #gad-rapor * { visibility: visible; }
          #gad-rapor { position: absolute; left: 0; top: 0; width: 100%; padding: 0 !important; }
          #gad-rapor, #gad-rapor * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          #gad-rapor .gad-yazdirma-yok { display: none !important; }
          #gad-rapor tr { break-inside: avoid; }
          #gad-rapor .gad-kart { break-inside: avoid; }
          #gad-rapor .gad-yeni-sayfa { break-before: page; margin-top: 0 !important; }
        }`}</style>

      {hata && <div style={{ ...kart, marginTop: 20, padding: 16, color: C.down, fontSize: 14 }}>{hata}</div>}
      {!hesap && !hata && <div style={{ marginTop: 40, color: C.muted, fontSize: 14 }}>Google Ads'ten çekiliyor… (20–40 sn sürebilir)</div>}

      {hesap && (
        <>
          {hesap.hatalar.map((a) => (
            <div key={a.customerId} style={{ ...kart, marginTop: 16, padding: 12, fontSize: 13, color: C.down }}>Hesap {a.customerId}: {a.error}</div>
          ))}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 14, marginTop: 22 }}>
            <Kpi etiket="Harcama" deger={tlShort(hesap.top.cost)} alt={`${hesap.kampanyalar.length} kampanya`} />
            <Kpi etiket="Raporlanan dönüşüm değeri" deger={tlShort(hesap.top.value)} alt={`ROAS ${roas(hesap.top.value, hesap.top.cost)} · ${nf(hesap.top.conv)} dönüşüm`} />
            <Kpi etiket="Yalnız satın alma değeri" deger={tlShort(hesap.top.satinAlmaDeger)} alt={`ROAS ${roas(hesap.top.satinAlmaDeger, hesap.top.cost)}`} />
            <Kpi etiket="Satın alma dışı, Dönüşümler'de" deger={tlShort(hesap.top.digerDeger)}
              alt={`Raporlanan değerin ${pct(hesap.top.value ? hesap.top.digerDeger / hesap.top.value : 0)}'i · ${nf(hesap.top.digerConv)} dönüşüm`}
              vurgu={hesap.top.digerDeger > 0} />
          </div>

          {sorunlu.length > 0 && (
            <div style={{ ...kart, marginTop: 16, padding: '14px 16px', background: C.warnSoft, borderColor: '#FED7AA', display: 'flex', gap: 10 }}>
              <AlertTriangle size={18} color={C.warn} style={{ flexShrink: 0, marginTop: 1 }} />
              <div style={{ fontSize: 13, color: C.ink2, lineHeight: 1.55 }}>
                <b style={{ color: C.warn }}>{sorunlu.length} kampanyada</b> satın alma dışı bir eylem (ya da birden fazla satın alma eylemi) "Dönüşümler" sütununa giriyor.
                Bu kampanyaların ROAS'ı şişik görünür ve akıllı teklif bu eylemlere göre optimize eder:{' '}
                {sorunlu.map((c) => c.ad).join(', ')}.
              </div>
            </div>
          )}

          <div className="gad-yazdirma-yok" style={{ display: 'flex', gap: 10, marginTop: 24, flexWrap: 'wrap', alignItems: 'center' }}>
            <Secici secenekler={[['value', 'Dönüşüm değeri'], ['conv', 'Dönüşüm']]} deger={metrik} onChange={setMetrik} />
            <Secici secenekler={[['primary', 'Dönüşümler (birincil)'], ['all', 'Tüm dönüşümler']]} deger={kapsam} onChange={setKapsam} />
            <span style={{ fontSize: 12, color: C.faint }}>
              {kapsam === 'primary' ? 'Panelde ve teklifte sayılan değer' : 'İkincil eylemler dahil, yalnız gözlem'}
            </span>
          </div>

          {grafik.length > 0 && (
            <div className="gad-kart" style={{ ...kart, marginTop: 14, padding: '18px 18px 8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                <div style={{ fontSize: 15, fontWeight: 600 }}>{metrik === 'value' ? 'Dönüşüm değeri' : 'Dönüşüm'}, eyleme göre</div>
                <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                  {sutunlar.map((e) => (
                    <span key={e.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: C.ink2 }}>
                      <span style={{ width: 10, height: 10, borderRadius: 3, background: hesap.renk.get(e.key) }} />{e.name}
                    </span>
                  ))}
                </div>
              </div>
              <ResponsiveContainer width="100%" height={Math.max(220, grafik.length * 30 + 40)}>
                <BarChart data={grafik} layout="vertical" margin={{ top: 12, right: 16, left: 8, bottom: 4 }} barCategoryGap={6}>
                  <CartesianGrid horizontal={false} stroke={C.hair} />
                  <XAxis type="number" tickFormatter={(v) => (metrik === 'value' ? tlShort(v) : nf(v))} tick={{ fontSize: 11, fill: C.faint }} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="ad" width={250} tick={{ fontSize: 12, fill: C.ink2 }} axisLine={false} tickLine={false} />
                  <Tooltip cursor={{ fill: C.accentSoft }} formatter={(v, k) => [bicim(v), hesap.veriliEylem.find((e) => e.key === k)?.name || k]}
                    contentStyle={{ borderRadius: 10, border: `1px solid ${C.line}`, fontFamily: FONT, fontSize: 12 }} />
                  {sutunlar.map((e) => <Bar key={e.key} dataKey={e.key} stackId="a" fill={hesap.renk.get(e.key)} />)}
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="gad-yeni-sayfa" style={{ ...kart, marginTop: 16, overflow: 'hidden' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, padding: '16px 18px 12px', flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 600 }}>Kampanya × dönüşüm eylemi</div>
                <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>
                  Her kampanyanın altında, dönüşüm değerinin hangi eylemden geldiği. Harcamaya göre sıralı.
                  Turuncu satır: satın alma dışı bir eylem panelde ve teklifte sayılan "Dönüşümler"e giriyor.
                </div>
              </div>
              <button className="gad-yazdirma-yok" onClick={() => setKapali(kapali.size ? new Set() : new Set(hesap.kampanyalar.map((c) => c.key)))} style={{
                ...kart, padding: '6px 12px', cursor: 'pointer', fontFamily: FONT, fontSize: 12, color: C.ink2, whiteSpace: 'nowrap'
              }}>{kapali.size ? 'Tümünü aç' : 'Tümünü kapat'}</button>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 1000 }}>
                <thead>
                  <tr>
                    <th style={{ ...th, textAlign: 'left' }}>Kampanya / eylem</th>
                    <th style={th}>Harcama</th>
                    <th style={th}>Dönüşüm</th>
                    <th style={th}>Dönüşüm değeri</th>
                    <th style={th}>Değer payı</th>
                    <th style={th}>ROAS</th>
                    <th style={th} title="İkincil eylemler dahil">Tüm dönüşümler</th>
                    <th style={th} title="İkincil eylemler dahil">Tüm değer</th>
                    <th style={{ ...th, textAlign: 'left' }}>Teklif hedefleri</th>
                  </tr>
                </thead>
                <tbody>
                  {hesap.kampanyalar.map((c) => {
                    const acik = !kapali.has(c.key);
                    const alt = Object.entries(c.eylem)
                      .map(([ek, b]) => ({ ek, ...b }))
                      .sort((x, y) => (y.category === 'PURCHASE') - (x.category === 'PURCHASE') || y.allValue - x.allValue || y.allConv - x.allConv);
                    const ac = () => setKapali((s) => { const n = new Set(s); if (n.has(c.key)) n.delete(c.key); else n.add(c.key); return n; });
                    return (
                      <React.Fragment key={c.key}>
                        <tr onClick={ac} style={{ cursor: 'pointer', background: '#FAFBFF' }}>
                          <td style={{ ...td, textAlign: 'left', color: C.ink, fontWeight: 600, borderTop: `1px solid ${C.line}` }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                              <ChevronRight size={14} color={C.faint} style={{ transform: acik ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }} />
                              {c.ad}
                              {c.sorun && <AlertTriangle size={13} color={C.warn} />}
                            </span>
                            <div style={{ fontSize: 11, color: C.faint, fontWeight: 400, marginLeft: 20 }}>
                              {KANAL[c.channel] || c.channel} · {STRATEJI[c.bidding] || c.bidding}{c.status !== 'ENABLED' ? ' · duraklatıldı' : ''}
                            </div>
                          </td>
                          <td style={{ ...td, color: C.ink, fontWeight: 600, borderTop: `1px solid ${C.line}` }}>{tlShort(c.cost)}</td>
                          <td style={{ ...td, color: C.ink, fontWeight: 600, borderTop: `1px solid ${C.line}` }}>{nf(c.conv, c.conv < 10 ? 1 : 0)}</td>
                          <td style={{ ...td, color: C.ink, fontWeight: 700, borderTop: `1px solid ${C.line}` }}>{tlShort(c.value)}</td>
                          <td style={{ ...td, borderTop: `1px solid ${C.line}` }} />
                          <td style={{ ...td, color: C.ink, fontWeight: 600, borderTop: `1px solid ${C.line}` }}>
                            {roas(c.value, c.cost)}
                            {c.digerDeger > 0 && <div style={{ fontSize: 11, color: C.muted, fontWeight: 400 }}>satın alma {roas(c.satinAlmaDeger, c.cost)}</div>}
                          </td>
                          <td style={{ ...td, borderTop: `1px solid ${C.line}` }}>{nf(c.allConv, c.allConv < 10 ? 1 : 0)}</td>
                          <td style={{ ...td, borderTop: `1px solid ${C.line}` }}>{tlShort(c.allValue)}</td>
                          <td style={{ ...td, textAlign: 'left', borderTop: `1px solid ${C.line}` }}>
                            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                              {c.hedef.map((h) => (
                                <span key={h} style={{
                                  fontSize: 11, padding: '2px 7px', borderRadius: 6, whiteSpace: 'nowrap',
                                  background: h === 'PURCHASE' ? C.accentSoft : '#FFEDD5', color: h === 'PURCHASE' ? C.accent : C.warn
                                }}>{katAd(h)}</span>
                              ))}
                            </div>
                          </td>
                        </tr>
                        {acik && alt.length === 0 && (
                          <tr><td colSpan={9} style={{ ...td, textAlign: 'left', paddingLeft: 40, color: C.faint, fontSize: 12 }}>Bu dönemde dönüşüm yok</td></tr>
                        )}
                        {acik && alt.map((b) => {
                          const risk = b.category !== 'PURCHASE' && b.conv > 0;
                          const renk = hesap.renk.get(b.ek) || C.faint;
                          return (
                            <tr key={b.ek} style={{ background: risk ? C.warnSoft : C.card }}>
                              <td style={{ ...td, textAlign: 'left', paddingLeft: 40 }}>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                                  <span style={{ width: 8, height: 8, borderRadius: 2, background: renk }} />
                                  <span style={{ color: risk ? C.warn : C.ink2, fontWeight: risk ? 600 : 400 }}>{b.action}</span>
                                  <span style={{ fontSize: 11, color: C.faint }}>{katAd(b.category)}{b.conv > 0 ? '' : ' · ikincil'}</span>
                                </span>
                              </td>
                              <td style={td} />
                              <td style={{ ...td, color: b.conv > 0 ? C.ink2 : C.faint }}>{b.conv > 0 ? nf(b.conv, b.conv < 10 ? 1 : 0) : '–'}</td>
                              <td style={{ ...td, color: risk ? C.warn : b.value > 0 ? C.ink : C.faint, fontWeight: b.value > 0 ? 600 : 400 }}>{b.value > 0 ? tlShort(b.value) : '–'}</td>
                              <td style={td}>
                                {b.value > 0 && c.value > 0 ? (
                                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end' }}>
                                    <span style={{ width: 54, height: 6, borderRadius: 3, background: C.hair, overflow: 'hidden', display: 'inline-block' }}>
                                      <span style={{ display: 'block', height: '100%', width: `${Math.min(100, (b.value / c.value) * 100)}%`, background: renk }} />
                                    </span>
                                    {pct(b.value / c.value)}
                                  </span>
                                ) : <span style={{ color: C.faint }}>–</span>}
                              </td>
                              <td style={{ ...td, color: b.value > 0 ? C.ink2 : C.faint }}>{b.value > 0 ? roas(b.value, c.cost) : '–'}</td>
                              <td style={{ ...td, color: C.muted }}>{b.allConv > 0 ? nf(b.allConv, b.allConv < 10 ? 1 : 0) : '–'}</td>
                              <td style={{ ...td, color: C.muted }}>{b.allValue > 0 ? tlShort(b.allValue) : '–'}</td>
                              <td style={td} />
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                  <tr style={{ background: '#F3F5FC' }}>
                    <td style={{ ...td, textAlign: 'left', fontWeight: 700, color: C.ink, borderTop: `1px solid ${C.line}` }}>Toplam</td>
                    <td style={{ ...td, fontWeight: 700, color: C.ink, borderTop: `1px solid ${C.line}` }}>{tlShort(hesap.top.cost)}</td>
                    <td style={{ ...td, fontWeight: 700, color: C.ink, borderTop: `1px solid ${C.line}` }}>{nf(hesap.top.conv)}</td>
                    <td style={{ ...td, fontWeight: 700, color: C.ink, borderTop: `1px solid ${C.line}` }}>{tlShort(hesap.top.value)}</td>
                    <td style={{ ...td, borderTop: `1px solid ${C.line}` }} />
                    <td style={{ ...td, fontWeight: 700, color: C.ink, borderTop: `1px solid ${C.line}` }}>
                      {roas(hesap.top.value, hesap.top.cost)}
                      <div style={{ fontSize: 11, color: C.muted, fontWeight: 400 }}>satın alma {roas(hesap.top.satinAlmaDeger, hesap.top.cost)}</div>
                    </td>
                    <td style={{ ...td, borderTop: `1px solid ${C.line}` }}>{nf(hesap.top.allConv)}</td>
                    <td style={{ ...td, borderTop: `1px solid ${C.line}` }}>{tlShort(hesap.top.allValue)}</td>
                    <td style={{ ...td, borderTop: `1px solid ${C.line}` }} />
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div style={{ ...kart, marginTop: 16, overflow: 'hidden' }}>
            <div style={{ padding: '16px 18px 4px', fontSize: 15, fontWeight: 600 }}>Dönüşüm eylemleri</div>
            <div style={{ padding: '0 18px 12px', fontSize: 12, color: C.muted }}>
              Hesaptaki ayarlar ve bu dönemin toplamları. Birincil = "Dönüşümler" sütununa girer; ikincil yalnız "Tüm dönüşümler"de görünür.
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 900 }}>
                <thead>
                  <tr>
                    <th style={{ ...th, textAlign: 'left' }}>Eylem</th>
                    <th style={{ ...th, textAlign: 'left' }}>Kategori</th>
                    <th style={{ ...th, textAlign: 'left' }}>Kaynak</th>
                    <th style={{ ...th, textAlign: 'left' }}>Durum</th>
                    <th style={{ ...th, textAlign: 'left' }}>Sayım</th>
                    <th style={{ ...th, textAlign: 'left' }}>Değer</th>
                    <th style={th}>Dönüşüm</th>
                    <th style={th}>Değer</th>
                    <th style={th}>Tüm dön.</th>
                    <th style={th}>Tüm değer</th>
                  </tr>
                </thead>
                <tbody>
                  {hesap.sira.map((e) => {
                    const birincil = e.primary && e.inConversions;
                    const risk = birincil && e.category !== 'PURCHASE';
                    return (
                      <tr key={e.key} style={{ background: risk && e.conv > 0 ? C.warnSoft : 'transparent' }}>
                        <td style={{ ...td, textAlign: 'left', color: C.ink, fontWeight: 500 }}>
                          {hesap.renk.get(e.key) && <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: hesap.renk.get(e.key), marginRight: 7 }} />}
                          {e.name}
                        </td>
                        <td style={{ ...td, textAlign: 'left' }}>{katAd(e.category)}</td>
                        <td style={{ ...td, textAlign: 'left' }}>{e.type ? kaynakAd(e.type) : '–'}</td>
                        <td style={{ ...td, textAlign: 'left' }}>
                          <span style={{
                            fontSize: 11, padding: '2px 7px', borderRadius: 6,
                            background: birincil ? (risk ? '#FFEDD5' : C.accentSoft) : C.hair,
                            color: birincil ? (risk ? C.warn : C.accent) : C.muted
                          }}>{birincil ? 'Birincil' : 'İkincil'}</span>
                          {e.status === 'HIDDEN' && <span style={{ fontSize: 11, color: C.faint, marginLeft: 6 }}>gizli</span>}
                        </td>
                        <td style={{ ...td, textAlign: 'left', fontSize: 12 }}>{e.counting === 'MANY_PER_CLICK' ? 'Her biri' : e.counting === 'ONE_PER_CLICK' ? 'Bir' : '–'}</td>
                        <td style={{ ...td, textAlign: 'left', fontSize: 12 }}>{e.alwaysDefault ? `Sabit ${nf(e.defaultValue)}` : e.type ? 'Dinamik' : '–'}</td>
                        <td style={td}>{e.conv > 0 ? nf(e.conv, e.conv < 10 ? 1 : 0) : '–'}</td>
                        <td style={{ ...td, color: risk && e.value > 0 ? C.warn : C.ink2, fontWeight: risk && e.value > 0 ? 600 : 400 }}>{e.value > 0 ? tlShort(e.value) : '–'}</td>
                        <td style={td}>{e.allConv > 0 ? nf(e.allConv, e.allConv < 10 ? 1 : 0) : '–'}</td>
                        <td style={td}>{e.allValue > 0 ? tlShort(e.allValue) : '–'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div style={{ fontSize: 12, color: C.faint, marginTop: 14, lineHeight: 1.6 }}>
            Satın alma ROAS = yalnız "Satın alma" kategorisindeki birincil dönüşüm değeri ÷ harcama. Bir dönem içinde kampanya hedefi
            değiştiyse, değişiklikten önceki günlerde sayılan satın alma dışı değer de Dönüşümler'de kalır; "Teklif hedefleri" bugünkü durumu gösterir.
          </div>
        </>
      )}
    </div>
  );
}
